import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ShieldCheck, ShieldX, Search, ArrowLeft, Lock, Eye } from 'lucide-react';
import '../Home/Home.css';
import './VerifyPage.css';

const ORIGIN = () => window.location.origin;

function fmtINR(n) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

// Minimum on-screen time for the scanning animation so it never just flashes.
const MIN_SCAN_MS = 1500;

export default function VerifyPage() {
  const { number: routeNumber } = useParams();
  const [input, setInput] = useState(routeNumber || '');
  const [status, setStatus] = useState('idle');   // idle | scanning | genuine | notfound | error
  const [result, setResult] = useState(null);      // minimal: { kind, number, date, total, customerMasked }
  const [full, setFull] = useState(null);          // revealed full details
  const [secret, setSecret] = useState('');
  const [secretState, setSecretState] = useState('idle'); // idle | checking | invalid
  const [isNavOpen, setIsNavOpen] = useState(false);

  const goTab = (tab) => { window.location.href = `${ORIGIN()}/?tab=${tab}`; };

  const callVerify = (payload) => {
    const call = httpsCallable(getFunctions(), 'verifyDocument');
    return call(payload).then(r => r.data);
  };

  const runVerify = useCallback(async (raw) => {
    const num = String(raw || '').trim();
    if (!num) return;
    setStatus('scanning');
    setResult(null);
    setFull(null);
    setSecret('');
    setSecretState('idle');

    const started = Date.now();
    try {
      const data = await callVerify({ number: num });
      const elapsed = Date.now() - started;
      if (elapsed < MIN_SCAN_MS) await new Promise(r => setTimeout(r, MIN_SCAN_MS - elapsed));

      if (data && data.found) {
        setResult(data);
        setStatus('genuine');
      } else {
        setStatus('notfound');
      }
    } catch (err) {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (routeNumber) runVerify(routeNumber);
  }, [routeNumber, runVerify]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = input.trim();
    if (!num) return;
    // Keep the URL clean/shareable but drive verification from state so we don't
    // need the number to be public in the DB.
    window.history.replaceState({}, document.title, `/verify/${num}`);
    runVerify(num);
  };

  const revealDetails = async (e) => {
    e.preventDefault();
    const s = secret.trim();
    if (!s || !result) return;
    setSecretState('checking');
    try {
      const data = await callVerify({ number: result.number, secret: s });
      if (data && data.secretValid) {
        setFull(data);
        setSecretState('idle');
      } else {
        setSecretState('invalid');
      }
    } catch {
      setSecretState('invalid');
    }
  };

  const kindLabel = result?.kind === 'quote' ? 'Quotation' : 'Invoice';
  // Total stays masked until the phone second factor unlocks the full details.
  const totalDisplay = full ? fmtINR(full.total) : 'XXXX';

  return (
    <div className="vf-page">
      <header className="home-header">
        <div className="home-logo" onClick={() => (window.location.href = ORIGIN())}>
          <h1>SQUARE</h1>
          <h2>COMPUTERS</h2>
        </div>
        <div className="nav-toggle" onClick={() => setIsNavOpen(!isNavOpen)}>☰</div>
        <nav className="home-nav">
          <ul>
            <li onClick={() => goTab('refurbished')}>REFURBISHED</li>
            <li onClick={() => goTab('components')}>PC COMPONENTS</li>
            <li onClick={() => goTab('desktopTower')}>DESKTOP TOWER</li>
            <li onClick={() => goTab('about')}>ABOUT</li>
            <li onClick={() => (window.location.href = `${ORIGIN()}/verify`)}>VERIFY BILL/QUOTE</li>
          </ul>
        </nav>
      </header>

      <div className={`mobile-nav ${isNavOpen ? 'active' : ''}`}>
        <span className="close-btn" onClick={() => setIsNavOpen(false)}>×</span>
        <ul>
          <li onClick={() => goTab('refurbished')}>REFURBISHED</li>
          <li onClick={() => goTab('components')}>PC COMPONENTS</li>
          <li onClick={() => goTab('desktopTower')}>DESKTOP TOWER</li>
          <li onClick={() => goTab('about')}>ABOUT</li>
          <li onClick={() => (window.location.href = `${ORIGIN()}/verify`)}>VERIFY BILL/QUOTE</li>
        </ul>
      </div>

      <main className="vf-main">
        <div className="vf-card">
          <h2 className="vf-title">Verify Bill / Quotation</h2>
          <p className="vf-subtitle">Enter your invoice or quotation number to check its authenticity.</p>

          <form className="vf-form" onSubmit={handleSubmit}>
            <div className="vf-input-wrap">
              <Search size={18} className="vf-input-icon" />
              <input
                className="vf-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="e.g. 482913"
                inputMode="numeric"
                disabled={status === 'scanning'}
              />
            </div>
            <button type="submit" className="vf-verify-btn" disabled={status === 'scanning' || !input.trim()}>
              {status === 'scanning' ? 'Verifying…' : 'Verify'}
            </button>
          </form>

          {status === 'scanning' && (
            <div className="vf-scan-stage">
              <div className="vf-scanner">
                <div className="vf-scan-ring" />
                <div className="vf-scan-ring vf-scan-ring-2" />
                <ShieldCheck size={44} className="vf-scan-core" />
                <div className="vf-scan-line" />
              </div>
              <p className="vf-scan-text">Checking our records…</p>
            </div>
          )}

          {status === 'genuine' && result && (
            <div className="vf-result vf-result-ok">
              <div className="vf-result-badge vf-badge-ok">
                <ShieldCheck size={30} />
              </div>
              <h3 className="vf-result-title">This {kindLabel} is Genuine</h3>
              <p className="vf-result-sub">{kindLabel} #{result.number} · issued by Square Computers</p>

              {/* Minimal, privacy-safe proof */}
              <div className="vf-detail-grid">
                <div><span className="vf-detail-label">Customer</span><span className="vf-detail-value">{result.customerMasked || '—'}</span></div>
                <div><span className="vf-detail-label">Date</span><span className="vf-detail-value">{result.date || '—'}</span></div>
                <div><span className="vf-detail-label">{kindLabel} #</span><span className="vf-detail-value">{result.number}</span></div>
                <div><span className="vf-detail-label">Total</span><span className="vf-detail-value">{totalDisplay}</span></div>
              </div>

              {/* Second factor to reveal full details */}
              {!full ? (
                <form className="vf-reveal" onSubmit={revealDetails}>
                  <div className="vf-reveal-head">
                    <Lock size={15} />
                    <span>Is this your document? Enter the phone number on it (or its last 4 digits) to see full details.</span>
                  </div>
                  <div className="vf-reveal-row">
                    <input
                      className="vf-input vf-reveal-input"
                      value={secret}
                      onChange={e => { setSecret(e.target.value); if (secretState === 'invalid') setSecretState('idle'); }}
                      placeholder="Phone number / last 4 digits"
                      inputMode="numeric"
                      disabled={secretState === 'checking'}
                    />
                    <button type="submit" className="vf-verify-btn" disabled={secretState === 'checking' || !secret.trim()}>
                      <Eye size={15} /> {secretState === 'checking' ? 'Checking…' : 'Reveal'}
                    </button>
                  </div>
                  {secretState === 'invalid' && (
                    <p className="vf-reveal-err">That phone number doesn't match this record.</p>
                  )}
                </form>
              ) : (
                <div className="vf-full">
                  <div className="vf-detail-grid">
                    <div><span className="vf-detail-label">Customer</span><span className="vf-detail-value">{full.customerName}</span></div>
                    <div><span className="vf-detail-label">Phone</span><span className="vf-detail-value">{full.phoneNumber}</span></div>
                    {full.address && (
                      <div className="vf-detail-full"><span className="vf-detail-label">Address</span><span className="vf-detail-value">{full.address}</span></div>
                    )}
                  </div>

                  {Array.isArray(full.items) && full.items.length > 0 && (
                    <div className="vf-table-wrap">
                      <table className="vf-table">
                        <thead>
                          <tr><th>Item</th><th>Qty</th><th>Price</th><th>Amount</th></tr>
                        </thead>
                        <tbody>
                          {full.items.map((it, i) => (
                            <tr key={i}>
                              <td>{it.itemName}</td>
                              <td>{it.quantity}</td>
                              <td>{fmtINR(it.price)}</td>
                              <td>{fmtINR(Number(it.quantity) * Number(it.price))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="vf-total-bar">
                <span>Total</span>
                <span className="vf-total-amount">{totalDisplay}</span>
              </div>
            </div>
          )}

          {status === 'notfound' && (
            <div className="vf-result vf-result-bad">
              <div className="vf-result-badge vf-badge-bad"><ShieldX size={30} /></div>
              <h3 className="vf-result-title">No record found</h3>
              <p className="vf-result-sub">
                We couldn't find a bill or quotation with that number. Please double-check the number, or contact us if you believe this document is genuine.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="vf-result vf-result-bad">
              <div className="vf-result-badge vf-badge-bad"><ShieldX size={30} /></div>
              <h3 className="vf-result-title">Something went wrong</h3>
              <p className="vf-result-sub">We couldn't check right now. Please try again in a moment.</p>
            </div>
          )}

          <button className="vf-back" onClick={() => (window.location.href = ORIGIN())}>
            <ArrowLeft size={15} /> Back to site
          </button>
        </div>
      </main>
    </div>
  );
}
